import { useId, useState } from "react";

import type { OrganisationRecord } from "../api/hooks";
import { useOrganisationSearch } from "../api/hooks";
import { useDebouncedValue } from "../lib/useDebouncedValue";

import styles from "./-OrganisationPicker.module.scss";

// One organisation somebody has been joined to, before the person exists. The
// affiliation cannot be written until there is a person to hang it on, so the form
// holds these and writes them after.
export interface ChosenOrganisation {
  id: string;
  name: string;
}

export function OrganisationPicker({
  chosen,
  onChoose,
  onRemove,
  disabled,
}: {
  chosen: ChosenOrganisation[];
  onChoose: (organisation: ChosenOrganisation) => void;
  onRemove: (organisationId: string) => void;
  disabled: boolean;
}) {
  const [search, setSearch] = useState("");
  const fieldId = useId();

  // The box stays on the raw value so typing is never laggy; the query follows
  // the settled one.
  const searchTerm = useDebouncedValue(search.trim(), 250);
  const { data, isFetching, isError } = useOrganisationSearch(searchTerm);

  // The search reads one page. Saying "nothing else matches" while the API is
  // holding a cursor is a claim about records this has not looked at.
  const truncated = (data?.nextCursor ?? "") !== "";

  // Already chosen ones are dropped from the results rather than shown as
  // selected: choosing one twice is not a thing the affiliations allow, and a result
  // that does nothing when clicked is worse than one that is not there.
  const chosenIds = new Set(chosen.map((organisation) => organisation.id));
  const results = (data?.items ?? []).filter(
    (record: OrganisationRecord) => !chosenIds.has(record.id),
  );

  return (
    <div className={styles.picker}>
      <label className={styles.label} htmlFor={`${fieldId}-search`}>
        Organisations
      </label>

      {chosen.length > 0 && (
        <ul className={styles.chosen}>
          {chosen.map((organisation) => (
            <li key={organisation.id} className={styles.chosenItem}>
              <span className={styles.chosenName}>{organisation.name}</span>
              <button
                type="button"
                className={styles.remove}
                disabled={disabled}
                onClick={() => onRemove(organisation.id)}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      <input
        id={`${fieldId}-search`}
        type="search"
        className={styles.search}
        placeholder="Search every organisation we know"
        value={search}
        disabled={disabled}
        onChange={(event) => setSearch(event.target.value)}
        // Enter in a search box means search, but this input sits inside the
        // person form, which has one submit button - so implicit submission
        // took the most natural gesture in the box and used it to write the
        // person, with whatever organisations happened to be chosen, and close
        // the panel. There is no undo for that from here.
        onKeyDown={(event) => {
          // Not while composing: Enter confirms a candidate in a Japanese or
          // Chinese input method, and swallowing it there would stop somebody
          // typing the name they are searching for.
          if (event.key === "Enter" && !event.nativeEvent.isComposing) {
            event.preventDefault();
          }
        }}
      />

      {/* Nothing is offered before somebody types. There are hundreds of
          bodies and nothing recording which of them anyone has worked on
          lately, so a default list would be an arbitrary page presented as a
          suggestion. */}
      {searchTerm === "" && (
        <div className={styles.state}>
          Type a name to find an organisation. Somebody can be saved without
          one.
        </div>
      )}

      {searchTerm !== "" && isError && (
        <div className={styles.state} role="alert">
          The organisations could not be searched. You can save without one and
          join them up later.
        </div>
      )}

      {searchTerm !== "" && !isError && results.length === 0 && (
        <div className={styles.state} role="status">
          {isFetching
            ? "Searching…"
            : truncated
              ? "No more on this page - narrow the search to see the rest."
              : // Not "no organisations": the ones already chosen are filtered
                // out of these results, so an empty list can mean they are all
                // picked already.
                "Nothing else matches that."}
        </div>
      )}

      {results.length > 0 && (
        <ul className={styles.results}>
          {results.map((record: OrganisationRecord) => (
            <li key={record.id}>
              <button
                type="button"
                className={styles.result}
                disabled={disabled}
                onClick={() => onChoose({ id: record.id, name: record.name })}
              >
                <span className={styles.resultName}>{record.name}</span>
                {/* What tells two similarly named bodies apart. Both are
                    optional on a record, so neither is drawn empty. */}
                {(record.county !== "" || record.kind !== "") && (
                  <span className={styles.resultMeta}>
                    {[record.kind, record.county].filter((part) => part !== "").join(" · ")}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
